import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOWN_ARROW } from '@angular/cdk/keycodes';
import {
   Directive,
   ElementRef,
   EventEmitter,
   HostBinding,
   HostListener,
   Inject,
   Input,
   OnDestroy,
   Optional,
   Output,
} from '@angular/core';
import {
   AbstractControl,
   ControlValueAccessor,
   NG_VALIDATORS,
   NG_VALUE_ACCESSOR,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import { Subscription } from 'rxjs';
import { LcsDateTimeAdapter } from '../configuration/date-time-adapter';
import { LCS_DATE_TIME_FORMATS, LcsDateTimeFormats } from '../configuration/date-time-formats';
import { createDateTimePickerError } from '../date-time-picker-errors';
import { LcsDateTimePickerComponent } from '../date-time-picker/date-time-picker.component';

/**
 * An event used for date time picker input and change events. We don't always have access to a native
 * input or change event because the event may have been triggered by the user clicking on the
 * calendar/time popup. For consistency, we always use DateTimePickerInputEvent instead.
 */
export class LcsDateTimePickerInputEvent<D> {
   /** The new value for the target datepicker input. */
   value: D | null;

   constructor(
      /** Reference to the dateTimePicker input directive that emitted the event. */
      public target: LcsDateTimePickerInputDirective<D>,
      /** Reference to the native input element associated with the datepicker input. */
      public targetElement: HTMLElement
   ) {
      this.value = this.target.value;
   }
}

@Directive({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: LcsDateTimePickerInputDirective,
        },
        {
            multi: true,
            provide: NG_VALIDATORS,
            useExisting: LcsDateTimePickerInputDirective,
        },
        { provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: LcsDateTimePickerInputDirective },
    ],
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'input[lcsDateTimePicker]',
    standalone: false
})
export class LcsDateTimePickerInputDirective<D> implements OnDestroy, ControlValueAccessor {
   @Input()
   @HostBinding('attr.placeholder')
   placeholder = 'mm/dd/yyyy hh:mm pm';

   /** Emits when a `change` event is fired on this `<input>`. */
   @Output() readonly dateTimeChange = new EventEmitter<LcsDateTimePickerInputEvent<D>>();

   /** Emits when an `input` event is fired on this `<input>`. */
   @Output() readonly dateTimeInput = new EventEmitter<LcsDateTimePickerInputEvent<D>>();

   /** Emits when the value changes (either due to user input or programmatic change). */
   @Output() valueChange = new EventEmitter<D | null>();

   /** Emits when the disabled state has changed */
   @Output() disabledChange = new EventEmitter<boolean>();

   @Input('lcsDateTimePicker')
   set dateTimePicker(value: LcsDateTimePickerComponent<D>) {
      this._dateTimePicker = value;
      this._dateTimePicker.registerInput(this);
      this.dateTimePickerSubscription.unsubscribe();
      this.dateTimePickerSubscription = this._dateTimePicker.selectedChanged.subscribe((selected: D) => {
         this.value = selected;
         this.cvaOnChange(selected);
         this.onTouched();
         this.dateTimeInput.emit(new LcsDateTimePickerInputEvent(this, this.elementRef.nativeElement));
         this.dateTimeChange.emit(new LcsDateTimePickerInputEvent(this, this.elementRef.nativeElement));
      });
   }

   get dateTimePicker(): LcsDateTimePickerComponent<D> {
      return this._dateTimePicker;
   }

   _dateTimePicker: LcsDateTimePickerComponent<D>;

   @Input()
   set value(value: D | null) {
      value = this.dateTimeAdapter.deserialize(value);
      this.lastValueValid = !value || this.dateTimeAdapter.isValid(value);

      value = this.getValidDateTimeOrNull(value);
      const oldDate = this.value;
      this._value = value;
      this.formatValue(value);

      if (!this.dateTimeAdapter.sameDate(oldDate, value)) {
         this.valueChange.emit(value);
      }
   }

   get value(): D | null {
      return this._value;
   }

   private _value: D | null;

   /** The minimum valid date. */
   @Input()
   set min(value: D | null) {
      this._min = this.getValidDateTimeOrNull(this.dateTimeAdapter.deserialize(value));
      this.validatorOnChange();
   }

   get min(): D | null {
      return this._min;
   }

   private _min: D | null;

   /** The maximum valid date. */
   @Input()
   set max(value: D | null) {
      this._max = this.getValidDateTimeOrNull(this.dateTimeAdapter.deserialize(value));
      this.validatorOnChange();
   }

   get max(): D | null {
      return this._max;
   }

   private _max: D | null;

   /** Whether the datepicker-input is disabled. */
   @Input()
   set disabled(value: boolean) {
      const newValue = coerceBooleanProperty(value);
      const element = this.elementRef.nativeElement;

      if (this._disabled !== newValue) {
         this._disabled = newValue;
         this.disabledChange.emit(newValue);
      }

      // We need to null check the `blur` method, because it's undefined during SSR.
      if (newValue && element.blur) {
         // Normally, native input elements automatically blur if they turn disabled. This behavior
         // is problematic, because it would mean that it triggers another change detection cycle,
         // which then causes a changed after checked error if the input element was focused before.
         element.blur();
      }
   }

   get disabled(): boolean {
      return !!this._disabled;
   }

   private _disabled: boolean;

   /** Function that can be used to filter out dates within the datepicker. */
   @Input()
   set dateFilter(value: (date: D | null) => boolean) {
      this._filter = value;
      this.validatorOnChange();
   }

   get dateFilter(): (date: D | null) => boolean {
      return this._filter;
   }

   private _filter: (date: D | null) => boolean;

   private dateTimePickerSubscription = Subscription.EMPTY;

   onTouched: () => void;

   private cvaOnChange: (value: D | null) => void;

   private validatorOnChange: () => void;

   /** The combined form control validator for this input. */
   private validator: ValidatorFn | null;

   /** Whether the last value set on the input was valid. */
   private lastValueValid = false;

   constructor(
      private elementRef: ElementRef<HTMLInputElement>,
      @Optional() public dateTimeAdapter: LcsDateTimeAdapter<D>,
      @Optional() @Inject(LCS_DATE_TIME_FORMATS) private dateFormats: LcsDateTimeFormats,
      @Optional() private formField: MatFormField // TODO: use @Inject(MAT_FORM_FIELD) once we're on @angular/material 9.2.0
   ) {
      this.validator = Validators.compose([this.parseValidator, this.minValidator, this.maxValidator]);

      if (!this.dateTimeAdapter) {
         throw createDateTimePickerError('LcsDateTimeAdapter');
      }

      if (!this.dateTimeAdapter) {
         throw createDateTimePickerError('LcsDateTimeFormats');
      }
   }

   @HostListener('keydown', ['$event'])
   onKeydown(event: KeyboardEvent) {
      const isAltDownArrow = event.altKey && event.keyCode === DOWN_ARROW;
      if (this.dateTimePicker && isAltDownArrow && !this.elementRef.nativeElement.readOnly) {
         this.dateTimePicker.open();
         event.preventDefault();
      }
   }

   @HostListener('input', ['$event.target.value'])
   onInput(value: string) {
      const lastValueWasValid = this.lastValueValid;
      let date = this.dateTimeAdapter.parse(value, this.dateFormats.parse.dateTimeInput);
      this.lastValueValid = !date || this.dateTimeAdapter.isValid(date);
      date = this.getValidDateTimeOrNull(date);

      if (!this.dateTimeAdapter.sameDate(date, this._value)) {
         this._value = date;
         this.cvaOnChange(date);
         this.valueChange.emit(date);
         this.dateTimeInput.emit(new LcsDateTimePickerInputEvent(this, this.elementRef.nativeElement));
      } else if (lastValueWasValid !== this.lastValueValid) {
         this.validatorOnChange();
      }
   }

   @HostListener('change')
   onChange() {
      this.dateTimeChange.emit(new LcsDateTimePickerInputEvent(this, this.elementRef.nativeElement));
   }

   @HostListener('blur')
   onBlur() {
      // Reformat the input only if we have a valid value.
      if (this.value) {
         this.formatValue(this.value);
      }

      this.onTouched();
   }

   ngOnDestroy(): void {
      this.dateTimePickerSubscription.unsubscribe();
   }

   registerOnValidatorChange(fn: () => void): void {
      this.validatorOnChange = fn;
   }

   validate(c: AbstractControl): ValidationErrors | null {
      return this.validator ? this.validator(c) : null;
   }

   /**
    * Gets the element that the date time picker popup should be connected to.
    * @return The element to connect the popup to.
    */
   getConnectedOverlayOrigin(): ElementRef {
      return this.formField ? this.formField.getConnectedOverlayOrigin() : this.elementRef;
   }

   // Implemented as part of ControlValueAccessor.
   writeValue(value: D): void {
      this.value = value;
   }

   // Implemented as part of ControlValueAccessor.
   registerOnChange(fn: (value: any) => void): void {
      this.cvaOnChange = fn;
   }

   // Implemented as part of ControlValueAccessor.
   registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
   }

   // Implemented as part of ControlValueAccessor.
   setDisabledState(isDisabled: boolean): void {
      this.disabled = isDisabled;
   }

   getThemePalette(): ThemePalette {
      return this.formField ? this.formField.color : undefined;
   }

   /** The form control validator for whether the input parses. */
   private parseValidator: ValidatorFn = (): ValidationErrors | null => {
      return this.lastValueValid ? null : { dateTimeParse: { text: this.elementRef.nativeElement.value } };
   };

   /** The form control validator for the min date. */
   private minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const controlValue = this.getValidDateTimeOrNull(this.dateTimeAdapter.deserialize(control.value));
      return !this.min || !controlValue || this.dateTimeAdapter.compareDate(this.min, controlValue) <= 0
         ? null
         : { dateTimePickerMin: { actual: controlValue, min: this.min } };
   };

   /** The form control validator for the max date. */
   private maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const controlValue = this.getValidDateTimeOrNull(this.dateTimeAdapter.deserialize(control.value));
      return !this.max || !controlValue || this.dateTimeAdapter.compareDate(this.max, controlValue) >= 0
         ? null
         : { dateTimePickerMax: { actual: controlValue, max: this.max } };
   };

   /** Formats a value and sets it on the input element. */
   private formatValue(value: D | null) {
      this.elementRef.nativeElement.value = value
         ? this.dateTimeAdapter.formatDateTime(value, this.dateFormats.display.dateTimeInput)
         : '';
   }

   /**
    * @param obj The object to check.
    * @returns The given object if it is both a date instance and valid, otherwise null.
    */
   private getValidDateTimeOrNull(obj: any): D | null {
      return this.dateTimeAdapter.isDateInstance(obj) && this.dateTimeAdapter.isValid(obj) ? obj : null;
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   // Accept `any` to avoid conflicts with other directives on `<input>` that
   // may accept different types.
   static ngAcceptInputType_value: any;

   static ngAcceptInputType_disabled: BooleanInput;
}
